/*
 * <cart-swap-add> — the cart swap row's transport (snippets/cart-swap-offer.liquid).
 * Spec: docs/CART_SWAP_ROW_BUILD.md §4.3.
 *
 * ── ONE REQUEST, ATOMIC ───────────────────────────────────────────────────────
 * The row's data-updates is a JSON map the server rendered for THIS cart state:
 * every line the bundle replaces at (quantity − 1) by its line KEY, plus the
 * bundle's variant id at 1. One POST to routes.cart_update_url applies the whole
 * map, writes the _swap_source cart note, and asks the Section Rendering API for
 * every cart region on the page in the same request (CartRegions.sectionsToRender(),
 * assets/cart-regions.js), so the drawer, /cart and the bubble can never disagree.
 * Nothing is removed first and added second: a failure leaves the cart as it was.
 *
 * ── THE RULES CARRIED FROM a4-cart.js (the ScribblePop tiles' transport) ──────
 *   - ONE WRITE IN FLIGHT per page. While one is pending every [data-swap-control]
 *     is aria-disabled and Dawn's own line-list lock (cart__items--disabled) is
 *     taken, so a stepper, an A4 "+" and a Swap can never interleave.
 *   - KEYS, NEVER INDEXES. Dawn's stepper edits by line position; a line removed
 *     in another tab then edits the wrong line silently. A key from another tab
 *     is refused by Shopify instead, and this element treats ANY doubtful response
 *     the same way: a status/errors body, a null `sections` payload, a response
 *     whose items still hold a replaced key at the old quantity or lack the bundle,
 *     or another writer announcing during the flight — refetch every region first
 *     (CartRegions.fetchAndRender), then show the message in the row's own slot.
 *     Shopify's own 422 description is kept; anything else is cartStrings.error.
 *   - IT NEVER OPENS THE DRAWER. Confirmation is the re-render, the bubble, the
 *     focus move and the live-region announcement.
 *   - source: 'cart-items' on the cartUpdate it publishes is the render-suppression
 *     flag cart.js keys on (every region is already rendered from this response),
 *     not provenance; provenance rides on origin: 'cart-swap'. Read
 *     cart-attach-add.js's publish site for the measured reason.
 *
 * ── PRE-FLIGHT (2026-10-05, Staging QA) ───────────────────────────────────────
 * Two Shopify behaviours the render cannot see, both measured on this store:
 *   - /cart/update resolves a key whose hash no longer exists by its VARIANT
 *     prefix. A row drawn for an older cart — a page restored from the
 *     back/forward cache, a second tab that bumped the quantity — therefore
 *     removes EVERY unit of the part for one bundle, with a 200. Only a key for
 *     a variant absent from the cart gets the 422.
 *   - /cart/update does not check availability: a bundle sold out since the
 *     render lands in the cart and is refused at checkout instead.
 * So before the write the element reads /cart.js and the bundle's product JSON
 * in parallel and writes only if the cart still holds exactly the line keys the
 * row was rendered with (data-cart-keys) and the bundle's variant is available.
 * Otherwise: every region refetched, a message, focus on the fresh row. A page
 * restored from the back/forward cache also redraws every region on pageshow.
 *
 * ── NO FORM ───────────────────────────────────────────────────────────────────
 * The row sits inside <form id="CartDrawer-Form"> / <form id="cart">; a nested
 * form is dropped by the parser. A button, data attributes and fetch, as the add
 * row does.
 *
 * ── AFTER THE SWAP ────────────────────────────────────────────────────────────
 * The re-render destroys this element. Everything the follow-up needs (surface,
 * announcement, variant id) is captured in the click's closure before the fetch,
 * so a failed swap leaves nothing behind for a later tap to inherit (D2). Focus
 * goes to the new bundle line, found by the variant id its quantity input carries
 * (the same locator cart-attach-add.js uses); the surface's live region announces.
 *
 * Globals used: routes / fetchConfig / publish / subscribe / PUB_SUB_EVENTS /
 * trapFocus / window.cartStrings (constants.js, pubsub.js, global.js,
 * layout/theme.liquid) and CartRegions (assets/cart-regions.js). Loaded with defer
 * from snippets/cart-drawer.liquid and sections/main-cart-items.liquid — twice on
 * /cart, hence both guards.
 */
if (!window.CartSwap) {
  window.CartSwap = (() => {
    const CONTROL = '[data-swap-control]';
    const DAWN_LOCK = 'cart__items--disabled';
    let inFlight = false;
    let raced = false;

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (inFlight && (!event || event.origin !== 'cart-swap')) raced = true;
      });
    }

    const lock = (locked) => {
      if (locked) {
        document.querySelectorAll(CONTROL).forEach((control) => {
          if (control.getAttribute('aria-disabled') === 'true') return;
          control.setAttribute('aria-disabled', 'true');
          control.setAttribute('data-swap-locked', '');
        });
      } else {
        document.querySelectorAll('[data-swap-locked]').forEach((control) => {
          control.removeAttribute('aria-disabled');
          control.removeAttribute('data-swap-locked');
        });
      }
      ['main-cart-items', 'CartDrawer-CartItems'].forEach((id) => {
        const list = document.getElementById(id);
        if (list) list.classList.toggle(DAWN_LOCK, locked);
      });
      inFlight = locked;
      if (locked) raced = false;
    };

    // A page restored from the back/forward cache shows the cart as it was when
    // the shopper left it; redraw every region from the server (the party-pack
    // band does the same where it mounts — two GETs there, harmless).
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted || inFlight || !window.CartRegions) return;
      CartRegions.fetchAndRender(CartRegions.sectionsToRender())
        .then(() => CartRegions.syncEmptyStateFromDom())
        .catch((error) => console.error(error));
    });

    return {
      get busy() {
        return inFlight || !!document.querySelector(`.${DAWN_LOCK}`);
      },
      get raced() {
        return raced;
      },
      lock,
    };
  })();
}

if (!customElements.get('cart-swap-add')) {
  // No locale key exists for either (as for the "Swap" label itself).
  const CHANGED_MESSAGE = 'Your cart changed. Check it and try again.';
  const SOLD_OUT_MESSAGE = 'Sorry, that bundle just sold out.';

  customElements.define(
    'cart-swap-add',
    class CartSwapAdd extends HTMLElement {
      constructor() {
        super();
        this.onClick = this.onClick.bind(this);
      }

      connectedCallback() {
        this.button = this.querySelector('[data-swap-control]');
        if (this.button) this.button.addEventListener('click', this.onClick);
      }

      disconnectedCallback() {
        if (this.button) this.button.removeEventListener('click', this.onClick);
      }

      onClick(event) {
        event.preventDefault();
        if (!this.button || this.button.getAttribute('aria-disabled') === 'true') return;
        if (window.CartSwap.busy || !window.CartRegions) return;

        let updates = null;
        try {
          updates = JSON.parse(this.dataset.updates || '');
        } catch (error) {
          updates = null;
        }
        const variantId = this.dataset.bundleVariantId;
        if (!updates || !variantId) return;

        // Captured before the fetch: the re-render detaches this element.
        const inDrawer = !!this.closest('cart-drawer');
        const scopeId = inDrawer ? 'CartDrawer' : 'main-cart-items';
        const liveRegionId = inDrawer ? 'CartDrawer-LiveRegionText' : 'cart-live-region-text';
        const announcement = this.dataset.announce || '';
        const source = this.dataset.swapSource || '';
        const bundleUrl = this.dataset.bundleUrl || '';
        const renderedKeys = (this.dataset.cartKeys || '').split(',').filter(Boolean);
        const sections = CartRegions.sectionsToRender();
        const generic =
          (window.cartStrings && window.cartStrings.error) ||
          'There was an error while updating your cart. Please try again.';

        const payload = {
          updates,
          sections: sections.map((entry) => entry.section),
          sections_url: window.location.pathname,
        };
        if (source) payload.attributes = { _swap_source: source };

        // One fresh photograph of every region, swapped in; empty-state flags off
        // the fresh DOM. Never rejects: a failed refetch leaves the regions as they
        // were, logged.
        const refetch = () =>
          CartRegions.fetchAndRender(sections)
            .then(() => CartRegions.syncEmptyStateFromDom())
            .catch((error) => console.error(error));

        // The message goes to whichever row for this bundle is in the page when the
        // write settles — a refetch has usually replaced this one. When no row is
        // left (the line or the bundle went with the refetch) the slot is gone too,
        // so the surface's live region carries the message instead (§8).
        const showError = (message) => {
          const scope = document.getElementById(scopeId);
          const current =
            scope && scope.querySelector(`cart-swap-add[data-bundle-variant-id="${variantId}"]`);
          if (current) {
            current.showError(message);
            return;
          }
          setTimeout(() => {
            const region = document.getElementById(liveRegionId);
            if (region) region.textContent = message;
          }, 100);
        };

        const announce = () =>
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-items',
            origin: 'cart-swap',
            productVariantId: variantId,
          });

        // Every refusal ends the same way: the true cart on screen first, then the
        // message, then focus back on the Swap button of this bundle's fresh row.
        // The page just changed under the shopper: other listeners re-read the cart.
        const refuse = (message, state) =>
          refetch().then(() => {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'cart-swap-add',
              productVariantId: variantId,
              errors: message,
              message: state && state.message,
            });
            announce();
            showError(message);
            CartSwapAdd.refocus(inDrawer, scopeId, variantId);
          });

        // The pre-flight (header): the cart must still hold exactly the lines this
        // row was rendered with, and the bundle's variant must still be available.
        // The cart read failing fails CLOSED (no blind write); the product read
        // failing is tolerated — checkout still refuses a sold-out line.
        const preflight = () =>
          Promise.all([
            fetch(`${(window.routes && routes.cart_url) || '/cart'}.js`, { cache: 'no-store' }).then(
              (response) => response.json()
            ),
            bundleUrl
              ? fetch(`${bundleUrl}.js`, { cache: 'no-store' })
                  .then((response) => (response.ok ? response.json() : null))
                  .catch(() => null)
              : Promise.resolve(null),
          ]).then(([cart, product]) => {
            const keys = (cart && Array.isArray(cart.items) ? cart.items : []).map((item) => item.key);
            const moved =
              keys.length !== renderedKeys.length || renderedKeys.some((key) => !keys.includes(key));
            const variant =
              product && Array.isArray(product.variants)
                ? product.variants.find((entry) => String(entry.id) === String(variantId))
                : null;
            const soldOut = !!product && (!variant || variant.available === false);
            return { moved, soldOut };
          });

        const write = () =>
          fetch(routes.cart_update_url, Object.assign(fetchConfig(), { body: JSON.stringify(payload) }))
            .then((response) => response.json())
            .then(
              (state) => {
                // /cart/update errors as {status, message, description} on the .js
                // route and as {errors} on the bare one; guard both. Then check the
                // write actually landed as asked: the bundle is in, no replaced key
                // still sits at a quantity above the one we sent.
                const failed = state.status || state.errors;
                const items = Array.isArray(state.items) ? state.items : [];
                const hasBundle = items.some((item) => String(item.variant_id) === String(variantId));
                const leftover = Object.keys(updates).some(
                  (key) =>
                    key !== String(variantId) &&
                    items.some((item) => item.key === key && item.quantity > updates[key])
                );
                if (failed || !hasBundle || leftover) {
                  const message = state.status === 422 && state.description ? state.description : generic;
                  return refuse(message, state);
                }

                let painted;
                if (state.sections) {
                  CartRegions.renderSections(sections, state.sections);
                  CartRegions.syncEmptyState(state.item_count === 0);
                  painted = Promise.resolve();
                } else {
                  painted = refetch();
                }
                return painted
                  .then(() => (window.CartSwap.raced ? refetch() : undefined))
                  .then(() => {
                    announce();
                    CartSwapAdd.afterSwap(inDrawer, scopeId, liveRegionId, variantId, announcement);
                  });
              },
              (error) => {
                // The request itself failed, or the body was not JSON (the bot wall's
                // HTML page). The write may or may not have landed: refetch, then say so.
                console.error(error);
                return refetch().then(() => showError(generic));
              }
            );

        window.CartSwap.lock(true);
        this.setLoading(true);
        this.showError(null);

        preflight()
          .then(
            ({ moved, soldOut }) => {
              if (moved) return refuse(CHANGED_MESSAGE);
              if (soldOut) return refuse(SOLD_OUT_MESSAGE);
              return write();
            },
            (error) => {
              console.error(error);
              return refuse(generic);
            }
          )
          .catch((error) => {
            console.error(error);
            showError(generic);
          })
          .finally(() => {
            window.CartSwap.lock(false);
            this.setLoading(false);
          });
      }

      setLoading(isLoading) {
        if (!this.button) return;
        const spinner = this.button.querySelector('.loading-overlay__spinner');
        this.button.classList.toggle('loading', isLoading);
        if (isLoading) {
          this.button.setAttribute('aria-disabled', 'true');
        } else {
          this.button.removeAttribute('aria-disabled');
        }
        if (spinner) spinner.classList.toggle('hidden', !isLoading);
      }

      showError(message) {
        const slot = this.querySelector('[data-swap-error]');
        if (!slot) return;
        slot.toggleAttribute('hidden', !message);
        slot.textContent = message || '';
      }

      /*
       * Focus the new bundle line (its quantity input carries the variant id) and
       * announce on the surface the tap came from. The live region is a node the
       * swap just recreated, so the text lands one tick later (a status region
       * filled in the same task it was inserted is not reliably announced).
       */
      static afterSwap(inDrawer, scopeId, liveRegionId, variantId, announcement) {
        const scope = document.getElementById(scopeId);
        if (scope) {
          const input = scope.querySelector(`[data-quantity-variant-id="${variantId}"]`);
          const line = input && input.closest('.cart-item');
          const target = line && line.querySelector('.cart-item__name');
          if (target) {
            if (inDrawer) {
              trapFocus(scope, target);
            } else {
              target.focus();
            }
          } else if (inDrawer) {
            const heading = scope.querySelector('.drawer__heading');
            if (heading) {
              heading.setAttribute('tabindex', '-1');
              trapFocus(scope, heading);
            }
          }
        }
        if (!announcement) return;
        setTimeout(() => {
          const region = document.getElementById(liveRegionId);
          if (region) region.textContent = announcement;
        }, 100);
      }

      /* After a refused swap: focus the Swap button of this bundle's row, re-rendered. */
      static refocus(inDrawer, scopeId, variantId) {
        const scope = document.getElementById(scopeId);
        if (!scope) return;
        const target = scope.querySelector(
          `cart-swap-add[data-bundle-variant-id="${variantId}"] [data-swap-control]`
        );
        if (!target) return;
        if (inDrawer) {
          trapFocus(scope, target);
        } else {
          target.focus();
        }
      }
    }
  );
}
